import { Link, Navigate, useParams } from "react-router-dom";
import { Check, Clock, Euro, ChevronRight, Dog, CalendarCheck } from "lucide-react";
import { SERVICE_META } from "../data/content";
import { Reveal } from "../components/motion";
import FAQAccordion from "../components/FAQAccordion";
import ContactForm from "../components/ContactForm";
import { useLang } from "../i18n";

const InfoCard = ({ icon: Icon, title, children, testid }) => (
    <div data-testid={testid} className="rounded-[1.5rem] border border-[#E3DCCF] bg-white p-6">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-olive-light text-olive-dark">
            <Icon size={18} />
        </span>
        <h3 className="font-serif mt-3 text-lg font-semibold text-ink">{title}</h3>
        <div className="mt-1.5 text-sm leading-relaxed text-[#525B53]">{children}</div>
    </div>
);

const ServiceDetail = () => {
    const { slug } = useParams();
    const { t } = useLang();
    const meta = SERVICE_META.find((m) => m.slug === slug);
    const service = t.services[slug];
    if (!meta || !service) return <Navigate to="/servicios" replace />;

    const Icon = meta.icon;
    const d = t.detail;

    return (
        <main data-testid={`service-page-${meta.slug}`} className="mx-auto max-w-7xl px-5 py-12 sm:px-8 lg:py-16">
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs font-semibold text-[#7C877E]">
                <Link to="/" className="transition-colors hover:text-terracotta">{d.inicio}</Link>
                <ChevronRight size={12} />
                <Link to="/servicios" className="transition-colors hover:text-terracotta">{d.servicios}</Link>
                <ChevronRight size={12} />
                <span className="text-terracotta">{service.nombre}</span>
            </nav>

            <div className="mt-8 grid items-center gap-10 lg:grid-cols-12">
                <div className="lg:col-span-6">
                    <Reveal y={16}>
                        <p className="inline-flex items-center gap-2 rounded-full bg-olive-light px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-olive-dark">
                            <Icon size={13} />
                            {service.tagline}
                        </p>
                        <h1 className="font-serif mt-4 text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
                            {service.nombre} {d.enMaresme}
                        </h1>
                        <p className="mt-4 max-w-lg text-base leading-relaxed text-[#525B53] sm:text-lg">
                            {service.desc}
                        </p>
                        <Link
                            to={`/contacto?servicio=${meta.slug}`}
                            data-testid="service-cta-reservar"
                            className="mt-7 inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-[15px] font-bold text-cream shadow-lg shadow-terracotta/25 transition-[background-color,transform] duration-200 hover:bg-terracotta-dark hover:-translate-y-0.5"
                        >
                            {d.reservar} {service.nombre.toLowerCase()}
                        </Link>
                    </Reveal>
                </div>
                <Reveal delay={0.1} className="lg:col-span-6">
                    <div className="arch-frame mx-auto max-w-md overflow-hidden border border-[#E3DCCF] shadow-xl shadow-ink/10 lg:max-w-none">
                        <img
                            src={meta.img}
                            alt={`${service.nombre} ${d.enMaresme}`}
                            className="aspect-[4/3] w-full object-cover"
                            loading="lazy"
                        />
                    </div>
                </Reveal>
            </div>

            <div className="mt-16 grid gap-10 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <Reveal>
                        <h2 className="font-serif text-2xl font-semibold text-ink sm:text-3xl">{d.incluye}</h2>
                        <ul className="mt-5 space-y-3">
                            {service.incluye.map((item) => (
                                <li key={item} className="flex items-start gap-3 text-[15px] leading-relaxed text-[#525B53]">
                                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-terracotta/10 text-terracotta">
                                        <Check size={14} />
                                    </span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>

                        <h2 className="font-serif mt-12 text-2xl font-semibold text-ink sm:text-3xl">{d.comoReservar}</h2>
                        <ol className="mt-5 space-y-4">
                            {d.pasos.map((paso, i) => (
                                <li key={i} className="flex items-start gap-4">
                                    <span className="font-serif flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-olive text-sm font-bold italic text-cream">
                                        {i + 1}
                                    </span>
                                    <p className="pt-1.5 text-[15px] leading-relaxed text-[#525B53]">{paso}</p>
                                </li>
                            ))}
                        </ol>
                    </Reveal>
                </div>

                <div className="space-y-5 lg:col-span-5">
                    <Reveal delay={0.05}>
                        <InfoCard icon={Dog} title={d.paraQuien} testid="service-para-quien">
                            {service.paraQuien}
                        </InfoCard>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <InfoCard icon={Clock} title={d.duracion} testid="service-duracion">
                            {service.duracion}
                        </InfoCard>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <InfoCard icon={Euro} title={d.precio} testid="service-precio">
                            {service.tarifas ? (
                                <ul className="space-y-2">
                                    {service.tarifas.map(([label, price]) => (
                                        <li key={label} className="flex items-baseline justify-between gap-3 border-b border-dashed border-[#E3DCCF] pb-2 last:border-0 last:pb-0">
                                            <span>{label}</span>
                                            <span className="font-serif whitespace-nowrap text-base font-semibold italic text-terracotta">{price}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <>
                                    <span className="font-serif text-lg font-semibold italic text-terracotta">{d.aConsultar}</span>
                                    <p className="mt-1.5">{d.aConsultarText}</p>
                                </>
                            )}
                            <ul className="mt-3 space-y-1 border-t border-[#E3DCCF] pt-3 text-xs text-[#7C877E]">
                                {d.notas.map((n) => (
                                    <li key={n}>· {n}</li>
                                ))}
                            </ul>
                        </InfoCard>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="rounded-[1.5rem] bg-olive p-6 text-cream">
                            <CalendarCheck size={20} className="text-sand" />
                            <h3 className="font-serif mt-3 text-lg font-semibold">{d.primerTitle}</h3>
                            <p className="mt-1.5 text-sm leading-relaxed text-cream/80">
                                {d.primerText}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>

            {service.faqs.length > 0 && (
                <section className="mx-auto mt-16 max-w-4xl">
                    <Reveal>
                        <h2 className="font-serif text-center text-2xl font-semibold text-ink sm:text-3xl">
                            {d.faqTitle} {service.nombre.toLowerCase()}
                        </h2>
                    </Reveal>
                    <FAQAccordion items={service.faqs} />
                </section>
            )}

            <section className="mt-20 grid gap-12 rounded-[2rem] border border-[#E3DCCF] bg-white p-6 sm:p-10 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <h2 className="font-serif text-2xl font-semibold text-ink sm:text-3xl">
                        {d.hablamos} {service.nombre.toLowerCase()}?
                    </h2>
                    <p className="mt-3 text-[15px] leading-relaxed text-[#525B53]">
                        {d.hablamosText}
                    </p>
                </div>
                <div className="lg:col-span-7">
                    <ContactForm servicioInicial={meta.slug} />
                </div>
            </section>
        </main>
    );
};

export default ServiceDetail;
